import { useState, useMemo } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do']
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

function toDateStr(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseDateStr(str) {
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isWeekend(date) {
  const day = date.getDay()
  return day === 0 || day === 6
}

export default function CalendarioSelector({ fechaInicio, fechaFin, onSeleccion, minDate, ausenciasExistentes = [] }) {
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  const [mesActual, setMesActual] = useState(fechaInicio ? parseDateStr(fechaInicio) : new Date(hoy.getFullYear(), hoy.getMonth(), 1))
  const [seleccionando, setSeleccionando] = useState(fechaInicio && fechaFin ? 'complete' : 'inicio')

  const inicio = fechaInicio ? parseDateStr(fechaInicio) : null
  const fin = fechaFin ? parseDateStr(fechaFin) : null

  const diasMes = useMemo(() => {
    const year = mesActual.getFullYear()
    const month = mesActual.getMonth()
    const primerDia = new Date(year, month, 1)
    const ultimoDia = new Date(year, month + 1, 0)

    // Día de la semana del primer día (0=Dom, 1=Lun...)
    let startDay = primerDia.getDay()
    startDay = startDay === 0 ? 6 : startDay - 1 // Ajustar a Lunes=0

    const dias = []

    // Días del mes anterior para rellenar
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i)
      dias.push({ date: d, otroMes: true })
    }

    // Días del mes actual
    for (let i = 1; i <= ultimoDia.getDate(); i++) {
      dias.push({ date: new Date(year, month, i), otroMes: false })
    }

    // Rellenar hasta completar filas de 7
    const remaining = 7 - (dias.length % 7)
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        dias.push({ date: new Date(year, month + 1, i), otroMes: true })
      }
    }

    return dias
  }, [mesActual])

  const ausenciasDates = useMemo(() => {
    const set = new Set()
    ausenciasExistentes.forEach(a => {
      let d = parseDateStr(a.fecha_inicio)
      const end = parseDateStr(a.fecha_fin)
      while (d <= end) {
        set.add(toDateStr(d))
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
      }
    })
    return set
  }, [ausenciasExistentes])

  const handleClickDia = (date) => {
    if (date < hoy && !isSameDay(date, hoy)) return

    const dateStr = toDateStr(date)

    if (seleccionando === 'inicio' || seleccionando === 'complete') {
      onSeleccion(dateStr, dateStr)
      setSeleccionando('fin')
    } else {
      // seleccionando === 'fin'
      if (date < inicio) {
        onSeleccion(dateStr, dateStr)
        setSeleccionando('fin')
      } else {
        onSeleccion(toDateStr(inicio), dateStr)
        setSeleccionando('complete')
      }
    }
  }

  const mesAnterior = () => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1))
  const mesSiguiente = () => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 1))

  const getDiaClass = (dia) => {
    const { date, otroMes } = dia
    const dateStr = toDateStr(date)
    const esPasado = date < hoy && !isSameDay(date, hoy)
    const esHoy = isSameDay(date, hoy)
    const esInicio = inicio && isSameDay(date, inicio)
    const esFin = fin && isSameDay(date, fin)
    const enRango = inicio && fin && date > inicio && date < fin
    const esFinDeSemana = isWeekend(date)
    const tieneAusencia = ausenciasDates.has(dateStr)

    if (otroMes) return 'text-gray-300 cursor-default'
    if (esPasado) return 'text-gray-300 cursor-not-allowed'
    if (tieneAusencia) return 'bg-red-100 text-red-400 cursor-not-allowed line-through'

    let cls = 'cursor-pointer transition-all '

    if (esInicio && esFin) {
      cls += 'bg-blue-600 text-white rounded-lg font-bold shadow-sm'
    } else if (esInicio) {
      cls += 'bg-blue-600 text-white rounded-l-lg font-bold'
    } else if (esFin) {
      cls += 'bg-blue-600 text-white rounded-r-lg font-bold'
    } else if (enRango) {
      cls += esFinDeSemana ? 'bg-blue-100 text-blue-400' : 'bg-blue-100 text-blue-700'
    } else if (esHoy) {
      cls += 'ring-2 ring-blue-400 ring-inset font-semibold text-blue-700 hover:bg-blue-50'
    } else if (esFinDeSemana) {
      cls += 'text-gray-400 hover:bg-gray-100'
    } else {
      cls += 'text-gray-700 hover:bg-blue-50'
    }

    return cls
  }

  // Contar días laborables en rango
  const diasLaborables = useMemo(() => {
    if (!inicio || !fin) return 0
    let count = 0
    let d = new Date(inicio)
    while (d <= fin) {
      if (!isWeekend(d)) count++
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
    }
    return count
  }, [inicio, fin])

  const diasNaturales = useMemo(() => {
    if (!inicio || !fin) return 0
    return Math.round((fin - inicio) / (1000 * 60 * 60 * 24)) + 1
  }, [inicio, fin])

  return (
    <div>
      {/* Header del calendario */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={mesAnterior} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <FiChevronLeft size={18} />
        </button>
        <h4 className="text-sm font-semibold text-gray-800">
          {MESES[mesActual.getMonth()]} {mesActual.getFullYear()}
        </h4>
        <button type="button" onClick={mesSiguiente} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <FiChevronRight size={18} />
        </button>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DIAS_SEMANA.map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>

      {/* Días del mes */}
      <div className="grid grid-cols-7 gap-0">
        {diasMes.map((dia, i) => (
          <button
            key={i}
            type="button"
            onClick={() => !dia.otroMes && handleClickDia(dia.date)}
            className={`h-9 text-sm flex items-center justify-center ${getDiaClass(dia)}`}
            disabled={dia.otroMes || (dia.date < hoy && !isSameDay(dia.date, hoy))}
          >
            {dia.date.getDate()}
          </button>
        ))}
      </div>

      {/* Resumen de selección */}
      {inicio && (
        <div className="mt-3 p-2.5 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <div className="text-blue-800">
              {isSameDay(inicio, fin || inicio) ? (
                <span className="font-medium">{inicio.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              ) : (
                <>
                  <span className="font-medium">{inicio.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                  <span className="mx-1.5 text-blue-400">→</span>
                  <span className="font-medium">{fin.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </>
              )}
            </div>
            <div className="text-right">
              <span className="font-bold text-blue-700">{diasLaborables}</span>
              <span className="text-blue-600 text-xs ml-1">día{diasLaborables !== 1 ? 's' : ''} lab.</span>
              {diasNaturales !== diasLaborables && (
                <span className="text-blue-400 text-xs ml-1">({diasNaturales} nat.)</span>
              )}
            </div>
          </div>
          {seleccionando === 'fin' && (
            <p className="text-xs text-blue-500 mt-1">Selecciona el último día</p>
          )}
        </div>
      )}

      {!inicio && (
        <p className="text-xs text-gray-400 text-center mt-3">Selecciona el primer día de la ausencia</p>
      )}
    </div>
  )
}
